import React, { useRef, useEffect } from "react";
import { Bold, Italic, Heading2, Heading3, List, ListOrdered, Quote, Link2, Undo, Redo, Eraser } from "lucide-react";

// Lightweight dependency-free WYSIWYG editor (contentEditable + execCommand).
// Stores HTML. React-19 safe. Sanitized on render by <RichText/>.
export default function RichTextEditor({ value, onChange, dir = "ltr", placeholder, testid }) {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || "")) {
      ref.current.innerHTML = value || "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emit = () => { if (ref.current) onChange(ref.current.innerHTML); };

  const cmd = (command, arg) => {
    ref.current?.focus();
    document.execCommand("styleWithCSS", false, false);
    document.execCommand(command, false, arg);
    emit();
  };

  const addLink = () => {
    const url = window.prompt("Link URL (include https://)");
    if (url) cmd("createLink", url);
  };

  const Btn = ({ icon: Icon, onClick, title }) => (
    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={onClick} title={title}
      className="p-2 text-muted-text hover:text-primary-gold hover:bg-obsidian transition-colors" data-testid={`rte-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <Icon size={15} strokeWidth={1.5} />
    </button>
  );

  return (
    <div className="border border-primary-gold/20 bg-obsidian" data-testid={testid}>
      <div className="flex flex-wrap items-center gap-0.5 border-b border-white/10 px-1 py-1 bg-charcoal">
        <Btn icon={Bold} title="Bold" onClick={() => cmd("bold")} />
        <Btn icon={Italic} title="Italic" onClick={() => cmd("italic")} />
        <span className="w-px h-5 bg-white/10 mx-1" />
        <Btn icon={Heading2} title="Heading" onClick={() => cmd("formatBlock", "H2")} />
        <Btn icon={Heading3} title="Subheading" onClick={() => cmd("formatBlock", "H3")} />
        <Btn icon={Quote} title="Quote" onClick={() => cmd("formatBlock", "BLOCKQUOTE")} />
        <span className="w-px h-5 bg-white/10 mx-1" />
        <Btn icon={List} title="Bullets" onClick={() => cmd("insertUnorderedList")} />
        <Btn icon={ListOrdered} title="Numbers" onClick={() => cmd("insertOrderedList")} />
        <Btn icon={Link2} title="Link" onClick={addLink} />
        <span className="w-px h-5 bg-white/10 mx-1" />
        <Btn icon={Eraser} title="Clear format" onClick={() => cmd("removeFormat")} />
        <Btn icon={Undo} title="Undo" onClick={() => cmd("undo")} />
        <Btn icon={Redo} title="Redo" onClick={() => cmd("redo")} />
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        dir={dir}
        onInput={emit}
        onBlur={emit}
        data-placeholder={placeholder || "Write here…"}
        className="rte-area min-h-[200px] max-h-[460px] overflow-y-auto p-4 text-sm text-soft-stone focus:outline-none"
      />
    </div>
  );
}
